import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import moment from 'moment';

// api / redux
import { useAppSelector } from '../../hooks/hooks';
import InstructorApi from '../../api/instructor.api';
import DepartmentsApi from '../../api/departments.api';
import Instructor from '../../models/instructor';

// comps
import { Box, Typography, TableCell, CircularProgress, Tooltip } from '@mui/material';
import GlobalButton from '../../components/global/button';
import GlobalInput from '../../components/global/input';
import FilterContainer from '../../components/layout/filterContainer';
import DataGrid from '../../components/grid/dataGrid';
import FilterOption from '../../components/global/filterOption';
import ContentContainer from '../../components/layout/contentContainer';
import { CustomTableCheckboxCell } from '../../components/grid/CustomTableCell';
import InstructorDialog from '../../components/dialogs/instructorDialog';

// icons
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as SortIcon } from '../../assets/icons/sort-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus-icon.svg';

// constants
import { defaultFilerOptionsArr } from '../../constants';
import { colors } from '../../theme';
import { accountTypes } from '../../constants';
import { MOMENT_DATE_DISPLAY_FORMAT } from '../../constants/date_format';

const Instructors = (props: any) => {
    const user = useAppSelector((state) => state.user);
    const appState = useAppSelector((state) => state.appState);
    const { school, term } = appState;
    const _instructorApi = new InstructorApi();
    const _departmentsApi = new DepartmentsApi();

    const isInstructor = user?.accountType === accountTypes.instructor;
    const isAdmin = user?.accountType === accountTypes.superAdmin || user?.accountType === accountTypes.admin;

    const [state, setState] = useState({
        programs: [],
        instructorDialog: {
            isOpen: false,
            data: null,
        },
        filterObject: {
            departmentCode: '',
            bcitId: '',
            instructorName: '',
            isActive: 'all',
            isPublic: 'all',
        },
        QRCodeDialog: {
            isOpen: false,
            data: null,
        },
        isClearAll: false,
    });

    const [instructors, setInstructors] = useState([]);
    const [resetPage, setResetPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(true);

    const loadInstructorData = async (schoolId: number) => {
        setIsLoading(true);
        let instructors = [];
        instructors = await _instructorApi.GetInstructorsBySchoolId(schoolId);

        if (isInstructor) {
            instructors = [instructors.find((i) => i.instructorId === user?.instructorId)];
        }
        setInstructors(instructors);
    };

    const loadProgramData = async (schoolId: number, termId: number) => {
        const programs = await _departmentsApi.GetDepartmentsBySchoolTerm(schoolId, termId);
        setState({
            ...state,
            programs,
        });
    };

    const handleFilter = (name: any, value: any) => {
        setState({
            ...state,
            filterObject: {
                ...state.filterObject,
                [name]: name === 'bcitId' || name === 'departmentCode' ? value.toUpperCase() : value,
            },
        });
        setButtonDisabled(false);
    };

    const handleClearAll = () => {
        let filterObject = { ...state.filterObject };
        Object.keys(filterObject).forEach((i) => (filterObject[i] = ''));

        setState({
            ...state,
            isClearAll: true,
            filterObject: {
                ...filterObject,
                isActive: 'all',
                isPublic: 'all',
            },
        });
        setResetPage(true);
        setButtonDisabled(true);
    };

    useEffect(() => {
        if (!Object.values(state.filterObject).every((o) => o === '')) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [state.filterObject]);

    useEffect(() => {
        setState({ ...state, isClearAll: false });
    }, [state.isClearAll]);

    const handleInstructorDialog = (data: any = null) => {
        setState({
            ...state,
            instructorDialog: {
                isOpen: !state.instructorDialog.isOpen,
                data,
            },
        });
    };

    const handleAddDialog = () => {
        const data = {
            adminNote: '',
            bcitId: '',
            department: {},
            email: '',
            firstName: '',
            isActive: true,
            lastName: '',
            middleName: '',
            office: '',
            optedOutOfPublicSite: false,
            phone: '',
            prefName: '',
            title: '',
        };
        handleInstructorDialog(data);
    };

    const addInstructor = async (instructor: Instructor) => {
        try {
            await _instructorApi.AddNewInstructor(instructor);
            handleInstructorDialog();
            toast.success(`Instructor ${instructor.firstName} ${instructor.lastName} successfully created.`);
            loadInstructorData(school?.id);
        } catch (error) {
            toast.error("Error adding instructor");
        }
    };

    const saveInstructor = async (instructor: Instructor) => {
        try {
            await _instructorApi.UpdateInstructor(instructor);
            handleInstructorDialog();
            toast.success(`Changes to instructor ${instructor.firstName} ${instructor.lastName} have been successfully saved.`);
            loadInstructorData(school?.id);
        } catch (error) {
            toast.error("Error saving instructor");
        }
    };

    const getGridData = () => {
        let instructorData = [...instructors];
        const { departmentCode, bcitId, instructorName, isActive, isPublic } = state.filterObject;

        // filter by program
        if (departmentCode) instructorData = instructorData.filter((instructor) => instructor.department?.departmentCode.toLowerCase().includes(departmentCode.toLowerCase()));

        // filter by bcit ID
        if (bcitId) instructorData = instructorData.filter((instructor) => instructor.bcitId.toLowerCase().includes(bcitId.toLowerCase()));

        // filter by instructor name
        if (instructorName)
            instructorData = instructorData.filter(
                (account) => account.firstName.toLowerCase().includes(instructorName.toLowerCase()) || account.lastName.toLowerCase().includes(instructorName.toLowerCase()),
            );

        // filter by isActive
        if (isActive !== 'all') instructorData = instructorData.filter((instructor) => instructor.isActive === isActive);

        // filter by opted out of public site
        if (isPublic !== 'all') instructorData = instructorData.filter((instructor) => instructor.optedOutOfPublicSite === isPublic);

        return instructorData;
    };

    useEffect(() => {
        if (school?.id && term?.termId) {
            loadInstructorData(school.id)
                .catch((error) => toast.error('Error loading instructors'))
                .finally(() => setIsLoading(false));
            loadProgramData(school.id, term.termId).catch((error) => toast.error('Error loading programs'));
        }
    }, [appState]);

    const renderInstructorGrid = (data: any, onChange: any) => {
        const { departmentCode } = data?.department;
        const { bcitId, firstName, lastName, dateAdded, dateLastScheduled } = data;

        return (
            <>
                <TableCell sx={{ width: '8%' }}>{departmentCode}</TableCell>
                <TableCell sx={{ width: '15%' }}>{bcitId}</TableCell>
                <TableCell sx={{ width: '25%' }}>
                    {firstName} {lastName}
                </TableCell>
                <TableCell sx={{ width: '12%' }}>{moment(dateAdded).format(MOMENT_DATE_DISPLAY_FORMAT)}</TableCell>
                <TableCell sx={{ width: '15%' }}>{!dateLastScheduled ? 'N/A' : moment(dateLastScheduled).format(MOMENT_DATE_DISPLAY_FORMAT)}</TableCell>
                {!isInstructor && (
                    <>
                        <CustomTableCheckboxCell {...{ data, name: 'isActive', onChange, disabled: false, width: '10%' }} />
                        <CustomTableCheckboxCell {...{ data, name: 'optedOutOfPublicSite', onChange, disabled: false, width: '15%' }} />
                    </>
                )}
            </>
        );
    };

    return (
        <>
            {!isInstructor && (
                <FilterContainer sx={{ justifyContent: 'space-between' }}>
                    <FlexBox>
                        <GlobalInput
                            label="filter"
                            icon={<FilterIcon />}
                            type="text"
                            name={'departmentCode'}
                            placeholder="Program"
                            sx={{ maxWidth: '85px' }}
                            value={state.filterObject.departmentCode}
                            onChange={handleFilter}
                        />
                        <GlobalInput type="text" name={'bcitId'} placeholder="BCIT ID" sx={{ maxWidth: '105px' }} value={state.filterObject.bcitId} onChange={handleFilter} />
                        <GlobalInput type="text" name={'instructorName'} placeholder="Instructor Name" value={state.filterObject.instructorName} onChange={handleFilter} />
                        <FilterOptionLabel variant="body2">Active</FilterOptionLabel>
                        <FilterOption
                            data={defaultFilerOptionsArr}
                            state={state.filterObject.isActive}
                            name={'label'}
                            onClickValue={'value'}
                            onOptionClick={(value: any) => handleFilter('isActive', value)}
                        />
                        <FilterOptionLabel variant="body2">Public</FilterOptionLabel>
                        <FilterOption
                            data={defaultFilerOptionsArr}
                            state={state.filterObject.isPublic}
                            name={'label'}
                            onClickValue={'value'}
                            onOptionClick={(value: any) => handleFilter('isPublic', value)}
                        />
                        <GlobalButton variant="round" text="clear all" secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} disabled={buttonDisabled} />
                    </FlexBox>
                    <GlobalButton text="add new instructor" variant="contained" startIcon={<PlusIcon />} onClick={handleAddDialog} />
                </FilterContainer>
            )}
            {instructors?.length === 0 && isLoading ? (
                <ContentContainer>
                    <FlexBox sx={{ justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            ) : (
                <DataGrid
                    isEdit
                    topHeight={20}
                    data={getGridData()}
                    columns={isInstructor ? instructorGridColumns.slice(0, 5) : instructorGridColumns}
                    dataItemKey="instructorId"
                    renderRows={renderInstructorGrid}
                    onEdit={handleInstructorDialog}
                    activeCell={state.instructorDialog.data}
                    resetPage={resetPage}
                    isClearSorting={state.isClearAll}
                    onHeaderClick={() => {
                        setButtonDisabled(false);
                    }}
                    // deleteDialog props
                    deleteDialogProps={{
                        isWarning: true,
                        buttonText: "delete",
                        deleteItem: "this instructor"
                    }}
                />
            )}
            {state.instructorDialog.isOpen && (
                <InstructorDialog
                    data={state.instructorDialog?.data}
                    programs={state.programs}
                    onClose={handleInstructorDialog}
                    onSave={saveInstructor}
                    onCreate={addInstructor}
                    isAdmin={isAdmin}
                    instructorId={user?.instructorId}
                />
            )}
        </>
    );
};

export default Instructors;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        & > * {
            margin-right: 10px;
        }
    }
`;

const FilterOptionLabel = styled(Typography)`
    && {
        color: ${colors.grey.dark};
        font-weight: 700;
    }
`;

const instructorGridColumns = [
    {
        id: 'department',
        label: 'Program',
        objectId: 'departmentCode',
    },
    {
        label: 'BCIT ID',
        disabled: true,
    },
    {
        id: 'firstName',
        label: 'Instructor Name',
    },
    {
        id: 'dateAdded',
        label: 'Date Added',
    },
    {
        id: 'dateLastScheduled',
        label: 'Date Last Scheduled',
    },
    {
        label: 'Active',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
    {
        label: 'Opted Out of Public',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
];
